// Centro de control (Sentinel): tipos, etiquetas y acceso a datos.
// API: api/modules/Sentinel/README.md

import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import api from '@/utils/api';
import pusher from '@/utils/pusher';

export type Result = 'granted' | 'denied' | 'pending' | 'unknown';
export type PointKind = 'vehicular' | 'pedestrian' | 'amenity';

export type Signal = {
  uuid: string;
  type: string;
  occurred_at: string;
  channel: string | null;
  credential: { type: string | null; code: string } | null;
  direction: string | null;
  duplicate: boolean;
  device: { uuid: string; name: string } | null;
  data: Record<string, any> | null;
  raw?: Record<string, any> | null; // solo super admin
};

export type Passage = {
  uuid: string;
  occurred_at: string;
  is_open: boolean;
  open_until: string | null;
  direction: 'entry' | 'exit' | null;
  result: Result;
  reason: string | null;
  method: string;
  methods: string[];
  source: string;
  decided_by: string | null;
  flags: string[];
  zone: { uuid: string; name: string } | null;
  access_point: { uuid: string; name: string; kind: PointKind } | null;
  unit: { uuid: string; name: string } | null;
  resident: { uuid: string; name: string } | null;
  vehicle: { uuid: string; brand: string | null; model: string | null; color: string | null; plate_number: string | null } | null;
  entry_pass: { uuid: string; short_code: string; guest_name: string | null; guest_type: string | null } | null;
  access_authorization: { uuid: string; folio: string } | null;
  credential: { uuid?: string | null; type: string | null; code: string; label?: string | null } | null;
  plate: string | null;
  guest: { name?: string; type?: string; vehicle?: string; comments?: string } | null;
  actor: { uuid: string; name: string } | null;
  source_device: { uuid: string; name: string; kind: string } | null;
  details: {
    door_opened_at?: string;
    confirmed_by_device?: boolean;
    // Foto del adeudo al pasar y lo que dice la política del punto (deny = se habría negado)
    debt?: { overdue_fees: number; amount: number };
    delinquency?: 'warn' | 'deny';
  } | null;
  signals?: Signal[];
};

export type AccessPoint = {
  uuid: string;
  name: string;
  kind: PointKind;
  direction: 'entry' | 'exit' | 'both';
  zone: { uuid: string; name: string } | null;
};

export type Summary = {
  totals: { total: number; granted: number; denied: number; entries: number; exits: number; guests: number };
  bucket: 'hour' | 'day';
  buckets: { start: string; granted: number; denied: number }[];
  methods: Record<string, number>;
  access_points: { access_point: string | null; total: number; denied: number; last_at: string | null }[];
};

export type Period = 'today' | 'yesterday' | '7d' | '30d';

export type Filters = {
  period: Period;
  zone_id?: string;
  access_point_id?: string;
  kind?: PointKind;
  direction?: 'entry' | 'exit';
  result?: 'granted' | 'denied';
  method?: string;
  source?: string;
  flag?: string;
  q?: string;
};

// --- Etiquetas -------------------------------------------------------------

export const METHOD_LABELS: Record<string, string> = {
  uhf_tag: 'Tag vehicular',
  rfid_card: 'Llavero',
  pin: 'PIN',
  plate: 'Placa',
  face: 'Rostro',
  fingerprint: 'Huella',
  guest_qr: 'QR de invitado',
  resident_qr: 'QR de residente',
  amenity_qr: 'QR de amenidad',
  manual: 'Registro manual',
  remote_open: 'Apertura remota',
  button: 'Botón',
};

export const METHOD_ICONS: Record<string, string> = {
  uhf_tag: 'car-sport-outline',
  rfid_card: 'key-outline',
  pin: 'keypad-outline',
  plate: 'scan-outline',
  face: 'happy-outline',
  fingerprint: 'finger-print-outline',
  guest_qr: 'qr-code-outline',
  resident_qr: 'qr-code-outline',
  amenity_qr: 'qr-code-outline',
  manual: 'create-outline',
  remote_open: 'radio-outline',
  button: 'radio-button-on-outline',
};

export const SOURCE_LABELS: Record<string, string> = {
  panel: 'Panel',
  totem: 'Tótem',
  guard_app: 'Vigilancia',
  airton: 'Tótem Airton',
  admin: 'Administración',
  resident_app: 'App del residente',
};

export const KIND_LABELS: Record<PointKind, string> = {
  vehicular: 'Vehicular',
  pedestrian: 'Peatonal',
  amenity: 'Amenidad',
};

export const KIND_ICONS: Record<PointKind, string> = {
  vehicular: 'car-outline',
  pedestrian: 'walk-outline',
  amenity: 'barbell-outline',
};

export const REASON_LABELS: Record<string, string> = {
  invalid_code: 'Código inválido',
  credential_unknown: 'Credencial no registrada',
  credential_inactive: 'Credencial desactivada',
  credential_expired: 'Credencial vencida',
  pass_invalid_date: 'Pase fuera de fecha',
  pass_used: 'Pase ya usado',
  qr_not_allowed: 'QR no permitido aquí',
  authorization_suspended: 'Autorización suspendida',
  authorization_inactive: 'Autorización no vigente',
  denied_by_device: 'Negado por el panel',
  delinquent: 'Adeudo',
  exception: 'Bloqueado por la administración',
};

export const SIGNAL_LABELS: Record<string, string> = {
  granted: 'El panel dio acceso',
  denied: 'El panel negó el acceso',
  credential_read: 'Credencial leída',
  credential_unregistered: 'Credencial no registrada',
  credential_expired: 'Credencial vencida',
  plate_read: 'Placa leída',
  remote_open: 'Apertura remota confirmada',
  door_button: 'Botón de salida',
  door_opened: 'La pluma abrió',
  door_state: 'Cambio en la puerta',
  presence: 'Presencia detectada',
  system_start: 'El equipo arrancó',
  system_reboot: 'El equipo reinició',
  aux_disconnected: 'Auxiliar desconectado',
  aux_shorted: 'Auxiliar en corto',
  other: 'Otro evento',
};

export const RESULT_LABELS: Record<Result, string> = {
  granted: 'Pasó',
  denied: 'Negado',
  pending: 'Pendiente',
  unknown: 'Sin decisión',
};

export const PERIOD_LABELS: Record<Period, string> = {
  today: 'Hoy',
  yesterday: 'Ayer',
  '7d': '7 días',
  '30d': '30 días',
};

/** Quién pasó, en una línea: invitado, residente, vivienda, credencial o placa. */
export function subjectOf(p: Passage): { title: string; subtitle: string | null } {
  const guest = p.entry_pass?.guest_name || p.guest?.name;
  const unit = p.unit?.name ?? null;

  if (guest) return { title: guest, subtitle: unit ? `Visita a ${unit}` : null };
  if (p.resident) return { title: p.resident.name, subtitle: unit };
  if (unit) return { title: unit, subtitle: p.vehicle?.plate_number ? `Placa ${p.vehicle.plate_number}` : null };
  if (p.credential) return { title: `${METHOD_LABELS[p.credential.type ?? ''] ?? 'Credencial'} ${p.credential.code}`, subtitle: 'Sin vivienda asignada' };
  if (p.plate) return { title: `Placa ${p.plate}`, subtitle: null };
  if (p.actor) return { title: p.actor.name, subtitle: SOURCE_LABELS[p.source] ?? null };

  return { title: 'Sin identificar', subtitle: null };
}

// --- Fechas ----------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');
export const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function periodRange(period: Period): { from: string; to: string } {
  const today = new Date();
  const daysAgo = (n: number) => { const d = new Date(today); d.setDate(d.getDate() - n); return ymd(d); };

  switch (period) {
    case 'yesterday': return { from: daysAgo(1), to: daysAgo(1) };
    case '7d': return { from: daysAgo(6), to: ymd(today) };
    case '30d': return { from: daysAgo(29), to: ymd(today) };
    default: return { from: ymd(today), to: ymd(today) };
  }
}

export const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

export const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const today = ymd(new Date());
  const yesterday = (() => { const y = new Date(); y.setDate(y.getDate() - 1); return ymd(y); })();
  if (ymd(d) === today) return 'Hoy';
  if (ymd(d) === yesterday) return 'Ayer';
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
};

export function ago(iso: string, now: number): string {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 10) return 'ahora';
  if (s < 60) return `hace ${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}

/** Re-render periódico para los relojes y los "hace X". */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// --- Datos -----------------------------------------------------------------

const base = (communityId: string) => `/communities/${communityId}/sentinel`;

export function filterParams(f: Filters): Record<string, string> {
  const { period, ...rest } = f;
  const params: Record<string, string> = { ...periodRange(period) };
  Object.entries(rest).forEach(([k, v]) => { if (v) params[k] = String(v); });
  return params;
}

export function usePassages(communityId: string | undefined, filters: Filters) {
  const params = useMemo(() => filterParams(filters), [filters]);

  return useInfiniteQuery({
    queryKey: ['sentinel.passages', communityId, params],
    queryFn: ({ pageParam }) => api
      .get(`${base(communityId!)}/passages`, { params: { ...params, cursor: pageParam, per_page: 40 } })
      .then(r => r.data as { data: Passage[]; meta: { next_cursor: string | null } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.meta?.next_cursor ?? undefined,
    enabled: !!communityId,
  });
}

export function usePassage(communityId: string | undefined, uuid: string | null) {
  return useQuery({
    queryKey: ['sentinel.passage', communityId, uuid],
    queryFn: () => api.get(`${base(communityId!)}/passages/${uuid}`).then(r => r.data.data as Passage),
    enabled: !!communityId && !!uuid,
  });
}

export function useSummary(communityId: string | undefined, period: Period) {
  return useQuery({
    queryKey: ['sentinel.summary', communityId, period],
    queryFn: () => api.get(`${base(communityId!)}/summary`, { params: periodRange(period) }).then(r => r.data as Summary),
    enabled: !!communityId,
    refetchInterval: 60_000,
  });
}

export function useAccessPoints(communityId: string | undefined) {
  return useQuery({
    queryKey: ['sentinel.access-points', communityId],
    queryFn: () => api.get(`${base(communityId!)}/access-points`).then(r => r.data.data as AccessPoint[]),
    enabled: !!communityId,
  });
}

/** ¿Un intento que llega en vivo entra en los filtros actuales? */
export function matchesFilters(p: Passage, f: Filters): boolean {
  const { from, to } = periodRange(f.period);
  const day = ymd(new Date(p.occurred_at));
  if (day < from || day > to) return false;
  if (f.zone_id && p.zone?.uuid !== f.zone_id) return false;
  if (f.access_point_id && p.access_point?.uuid !== f.access_point_id) return false;
  if (f.kind && p.access_point?.kind !== f.kind) return false;
  if (f.direction && p.direction !== f.direction) return false;
  if (f.result && p.result !== f.result) return false;
  if (f.method && !p.methods.includes(f.method)) return false;
  if (f.source && p.source !== f.source) return false;
  if (f.flag && !(p.flags ?? []).includes(f.flag)) return false;
  if (f.q) {
    const q = f.q.toLowerCase();
    const haystack = [p.credential?.code, p.plate, p.guest?.name, p.entry_pass?.guest_name, p.unit?.name]
      .filter(Boolean).join(' ').toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}

export type LiveStatus = 'connecting' | 'live' | 'offline';

/**
 * Intentos en vivo por Pusher (`private-sentinel.{community}.passages`). `onReconnect` avisa
 * cuando la conexión vuelve, para recargar lo que se pudo haber perdido.
 */
export function useLivePassages(
  communityId: string | undefined,
  handlers: { onPassage: (p: Passage, created: boolean) => void; onReconnect: () => void },
): LiveStatus {
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!communityId) return;

    const channelName = `private-sentinel.${communityId}.passages`;
    const channel = pusher.subscribe(channelName);
    let wasOffline = false;

    channel.bind('passage.created', (data: any) => ref.current.onPassage(data.passage, true));
    channel.bind('passage.updated', (data: any) => ref.current.onPassage(data.passage, false));
    channel.bind('pusher:subscription_succeeded', () => setStatus('live'));
    channel.bind('pusher:subscription_error', () => setStatus('offline'));

    const onState = ({ current }: { current: string }) => {
      if (current === 'connected') {
        setStatus('live');
        if (wasOffline) ref.current.onReconnect();
        wasOffline = false;
      } else if (current === 'unavailable' || current === 'failed' || current === 'disconnected') {
        setStatus('offline');
        wasOffline = true;
      }
    };
    pusher.connection.bind('state_change', onState);

    return () => {
      pusher.connection.unbind('state_change', onState);
      channel.unbind_all();
      pusher.unsubscribe(channelName);
    };
  }, [communityId]);

  return status;
}
